import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWSRefresh } from '../contexts/WebSocketContext';
import { Box, Typography, Grid, Card, CardContent, Avatar, Chip, LinearProgress, IconButton, Skeleton, Alert, Fab } from '@mui/material';
import {
  Assignment as TicketsIcon,
  Email as EmailIcon, PhotoCamera as PictureIcon, Description as DocumentIcon, Inbox as PortalIcon, Visibility as SeenIcon,
  CheckCircle as CompletedIcon,
  Warning as AlertsIcon,
  People as TechniciansIcon,
  TrendingUp,
  AccessTime,
  AddCircle as AddIcon,
  LocalShipping,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import api, { ticketsApi, dashboardApi, KanbanTicket, KanbanBoard } from '../services/api';
import { CC } from '../theme/theme';
import { useDataRefresh } from '../contexts/DataSyncContext';
import TripVerifyDialog from '../components/TripVerifyDialog';   // stop verification (2026-09-05)

// Dashboard stats interface
interface DashboardStats {
  unassigned_count: number;
  completed_today: number;
  alerts_count: number;
  active_technicians: number;
  priority_breakdown: {
    urgent: number;
    high: number;
    medium: number;
    low: number;
  };
}

interface StatCardProps {
  title: string;
  value: number;
  icon: JSX.Element;
  gradient: string;
  trend?: string;
  subtitle?: string;
  loading?: boolean;
  onClick?: () => void;
}

function StatCard({ title, value, icon, gradient, trend, subtitle, loading, onClick }: StatCardProps) {
  return (
    <Card
      onClick={onClick}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.15s, box-shadow 0.15s',
        '&:hover': onClick ? { transform: 'translateY(-2px)', boxShadow: 4 } : {},
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: gradient,
        },
      }}
    >
      <CardContent sx={{ pt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
                fontWeight: 700,
                letterSpacing: '0.1em',
                fontSize: '0.75rem',
              }}
            >
              {title}
            </Typography>
            {loading ? (
              <Skeleton width={80} height={48} />
            ) : (
              <Typography
                variant="h3"
                sx={{
                  mt: 1,
                  fontWeight: 700,
                  background: gradient,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {value}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              background: gradient,
              boxShadow: '0px 8px 24px rgba(0,0,0,0.12)',
            }}
          >
            {icon}
          </Avatar>
        </Box>
        {trend && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TrendingUp sx={{ fontSize: 16, color: 'success.main' }} />
            <Typography variant="caption" color="success.main" fontWeight={600}>
              {trend}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              vs last week
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

interface TicketItemProps {
  ticket: KanbanTicket;
}

function TicketItem({ ticket }: TicketItemProps) {
  const priorityColors: Record<string, 'error' | 'warning' | 'info' | 'success'> = {
    urgent: 'error',
    high: 'error',
    medium: 'warning',
    low: 'info',
  };

  return (
    <Box
      onClick={() => window.location.href = `/tickets?id=${ticket.id}`}
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        background: 'background.paper',
        transition: 'all 0.2s ease-in-out',
        cursor: 'pointer',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: '0px 4px 12px rgba(0,0,0,0.08)',
          transform: 'translateX(4px)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          {ticket.ticket_number}
        </Typography>
        <Chip
          label={ticket.priority.toUpperCase()}
          size="small"
          color={priorityColors[ticket.priority] || 'default'}
          sx={{ fontWeight: 700, fontSize: '0.7rem' }}
        />
      </Box>
      <Typography variant="body2" color="text.primary" fontWeight={500} sx={{ mb: 1 }}>
        {ticket.title}
      </Typography>
      {ticket.customer_name && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {ticket.customer_name}
        </Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
        <Typography variant="caption" color="text.secondary">
          {ticket.time_since_created || 'Just now'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          •
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {ticket.status}
        </Typography>
      </Box>
      
    </Box>
  );
}


// ---- Incoming alerts (emails to tech@, customer pictures/documents, portal requests). Click = open it + log who viewed. 2026-09-04
interface IncomingAlert { id: number; kind: string; source?: string; title: string; detail?: string; url?: string; created_at: string;
  view_count: number; viewed_by_me: boolean; last_viewer?: string; last_viewed_at?: string; }
const KIND_ICON: Record<string, JSX.Element> = { email: <EmailIcon fontSize="small" />, picture: <PictureIcon fontSize="small" />,
  document: <DocumentIcon fontSize="small" />, ticket: <PortalIcon fontSize="small" /> };
const KIND_LABEL: Record<string, string> = { email: 'Email', picture: 'Picture', document: 'Document', ticket: 'Portal request', invalid: 'Closed ticket', trip_verify: 'Verify stop' };
function ago(iso: string) {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z').getTime()) / 60000));
  if (m < 1) return 'just now'; if (m < 60) return `${m} min ago`; const h = Math.round(m / 60); if (h < 24) return `${h} h ago`;
  return new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z').toLocaleString();
}
function IncomingAlertItem({ a, onOpen }: { a: IncomingAlert; onOpen: (a: IncomingAlert) => void }) {
  const fresh = !a.viewed_by_me;
  return (
    <Box onClick={() => onOpen(a)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(a); }}
      sx={{ p: 1.5, mb: 1.5, borderRadius: 2, borderLeft: '4px solid', borderLeftColor: fresh ? CC.red : CC.line, cursor: 'pointer',
        background: fresh ? '#FFF7F7' : 'background.paper', boxShadow: '0px 2px 8px rgba(0,0,0,0.06)', display: 'flex', gap: 1.5, alignItems: 'flex-start',
        '&:hover': { boxShadow: '0px 4px 14px rgba(13,34,51,0.14)' } }}>
      <Avatar sx={{ width: 32, height: 32, bgcolor: fresh ? CC.red : CC.navy, color: '#fff' }}>{KIND_ICON[a.kind] || <DocumentIcon fontSize="small" />}</Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" fontWeight={fresh ? 700 : 500} sx={{ color: CC.ink }} noWrap title={a.title}>{a.title}</Typography>
          {fresh && <Chip label="NEW" size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: CC.red, color: '#fff' }} />}
          <Chip label={KIND_LABEL[a.kind] || a.kind} size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
        </Box>
        {a.detail && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>{a.detail}</Typography>}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
          {ago(a.created_at)}
          {a.view_count > 0 && <><SeenIcon sx={{ fontSize: 14, ml: 1 }} /> seen by {a.last_viewer}{a.view_count > 1 ? ` +${a.view_count - 1}` : ''}{a.last_viewed_at ? ` · ${ago(a.last_viewed_at)}` : ''}</>}
          {a.view_count === 0 && <span style={{ marginLeft: 8, color: CC.red, fontWeight: 600 }}>nobody has opened this yet</span>}
        </Typography>
      </Box>
    </Box>
  );
}

interface AlertItemProps {
  type: 'warning' | 'error' | 'info';
  message: string;
  time: string;
}

function AlertItem({ type, message, time }: AlertItemProps) {
  const alertColors = {
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#3b82f6',
  };

  return (
    <Box
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 2,
        borderLeft: '4px solid',
        borderLeftColor: alertColors[type],
        background: 'background.paper',
        boxShadow: '0px 2px 8px rgba(0,0,0,0.06)',
      }}
    >
      <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
        {message}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {time}
      </Typography>
      
    </Box>
  );
}

interface TechnicianItemProps {
  name: string;
  status: 'active' | 'break' | 'offline';
  tickets: number;
  progress: number;
}

function TechnicianItem({ name, status, tickets, progress }: TechnicianItemProps) {
  const statusColors = {
    active: 'success.main',
    break: 'warning.main',
    offline: 'error.main',
  };

  return (
    <Box sx={{ mb: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              background: 'linear-gradient(180deg, #1E4A6B, #0D2233)',
              fontSize: '0.875rem',
              fontWeight: 700,
            }}
          >
            {name.split(' ').map(n => n[0]).join('')}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: statusColors[status],
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {status}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Chip
          label={`${tickets} tickets`}
          size="small"
          sx={{ fontWeight: 600, fontSize: '0.75rem' }}
        />
      </Box>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 6,
          borderRadius: 3,
          bgcolor: 'grey.200',
          '& .MuiLinearProgress-bar': {
            borderRadius: 3,
            background: 'linear-gradient(90deg, #FF4A4A, #B00010)',
          },
        }}
      />
      
    </Box>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [incoming, setIncoming] = useState<IncomingAlert[]>([]);
  const [incomingUnviewed, setIncomingUnviewed] = useState(0);
  const loadIncoming = useCallback(() => {
    api.get('/alerts/incoming', { params: { limit: 25 } }).then((r: any) => { setIncoming(r.data.alerts || []); setIncomingUnviewed(r.data.unviewed || 0); }).catch(() => {});
  }, []);
  useEffect(() => { loadIncoming(); const iv = setInterval(loadIncoming, 30000); return () => clearInterval(iv); }, [loadIncoming]);
  const [verifyTrip, setVerifyTrip] = useState<number | null>(null);
  const openIncoming = async (a: IncomingAlert) => {
    let url = a.url || '/';
    try { const r = await api.post(`/alerts/incoming/${a.id}/view`); url = r.data?.url || url; } catch { /* still open it */ }
    setIncoming((list) => list.map((x) => x.id === a.id ? { ...x, viewed_by_me: true, view_count: x.view_count + (x.viewed_by_me ? 0 : 1) } : x));
    const vm = /verify=(\d+)/.exec(url || '');
    if (a.kind === 'trip_verify' && vm) { setVerifyTrip(Number(vm[1])); return; }
    navigate(url);
  };
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [unassignedTickets, setUnassignedTickets] = useState<KanbanTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      // Fetch kanban data first - this is the primary data source
      const kanbanRes = await ticketsApi.getKanbanBoard();
      const kanbanData = kanbanRes.data as KanbanBoard;
      
      // Try to get dashboard stats, but fall back to calculating from kanban data
      let statsData: DashboardStats | null = null;
      try {
        const statsRes = await dashboardApi.getStats();
        if (statsRes.data && typeof statsRes.data === 'object') {
          statsData = statsRes.data;
        }
      } catch {
        // Dashboard API not available, will calculate from kanban data
        console.log('Dashboard stats API not available, calculating from kanban data');
      }
      
      // Calculate stats from kanban data if API didn't return expected format
      const calculatedStats: DashboardStats = statsData || {
        unassigned_count: kanbanData.unassigned?.length || 0,
        completed_today: kanbanData.completed_today?.length || 0,
        alerts_count: 0,
        active_technicians: 3,
        priority_breakdown: {
          urgent: kanbanData.unassigned?.filter((t: KanbanTicket) => t.priority === 'urgent').length || 0,
          high: kanbanData.unassigned?.filter((t: KanbanTicket) => t.priority === 'high').length || 0,
          medium: kanbanData.unassigned?.filter((t: KanbanTicket) => t.priority === 'medium').length || 0,
          low: kanbanData.unassigned?.filter((t: KanbanTicket) => t.priority === 'low').length || 0,
        },
      };
      setStats(calculatedStats);
      setUnassignedTickets(kanbanData.unassigned?.slice(0, 5) || []);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Subscribe to data refresh events from other pages
  useDataRefresh(['tickets', 'dashboard'], () => fetchData(true));
  // Auto-refresh every 30 seconds


  
  // WebSocket push — instant refresh on any change
  useWSRefresh(['ticket:assigned', 'ticket:updated', 'ticket:status', 'ticket:created', 'ticket:deleted', 'schedule:updated'], () => { fetchData(true); loadIncoming(); });

  useEffect(() => {
    fetchData();
    // Refresh every 30 seconds
    const interval = setInterval(() => fetchData(true), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Generate alerts from stats
  const alerts = stats ? [
    ...(stats.priority_breakdown.urgent > 0 ? [{
      type: 'error' as const,
      message: `${stats.priority_breakdown.urgent} urgent ticket(s) require immediate attention`,
      time: 'Now',
    }] : []),
    ...(stats.priority_breakdown.high > 0 ? [{
      type: 'warning' as const,
      message: `${stats.priority_breakdown.high} high priority ticket(s) pending`,
      time: '5 mins ago',
    }] : []),
    ...(stats.unassigned_count > 5 ? [{
      type: 'info' as const,
      message: `${stats.unassigned_count} tickets in queue need assignment`,
      time: '10 mins ago',
    }] : []),
  ] : [];

  // Mock technicians (will be replaced with real API later)
  const technicians = [
    { name: 'John Smith', status: 'active' as const, tickets: 3, progress: 75 },
    { name: 'Sarah Johnson', status: 'active' as const, tickets: 2, progress: 60 },
    { name: 'Mike Davis', status: 'break' as const, tickets: 1, progress: 30 },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography
            variant="h4" sx={{
              fontWeight: 700,
              color: '#0D2233',
              mb: 0.5,
            }}
          >
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Welcome back! Here's what's happening today.
          </Typography>
        </Box>
        <IconButton
          onClick={() => fetchData()}
          disabled={loading}
          sx={{
            background: 'linear-gradient(180deg, #1E4A6B, #0D2233)',
            color: '#fff',
            '&:hover': {
              background: 'linear-gradient(180deg, #123A54, #0A1A28)',
            },
            '&:disabled': {
              background: 'grey.300',
            },
          }}
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: { xs: 2, md: 3 } }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={{ xs: 2, sm: 2, md: 3 }} sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Unassigned Tickets"
            value={stats?.unassigned_count ?? 0}
            icon={<TicketsIcon sx={{ fontSize: 28 }} />}
            gradient="linear-gradient(180deg, #1E4A6B, #0D2233)"
            subtitle="Awaiting assignment"
            loading={loading}
            onClick={() => navigate('/tickets?status=unassigned')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Completed Today"
            value={stats?.completed_today ?? 0}
            icon={<CompletedIcon sx={{ fontSize: 28 }} />}
            gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
            subtitle="Tasks finished"
            loading={loading}
            onClick={() => navigate('/tickets?status=completed')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Alerts"
            value={stats?.alerts_count ?? 0}
            icon={<AlertsIcon sx={{ fontSize: 28 }} />}
            gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
            subtitle="Require attention"
            loading={loading}
            onClick={() => navigate('/map')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Technicians"
            value={stats?.active_technicians ?? 0}
            icon={<TechniciansIcon sx={{ fontSize: 28 }} />}
            gradient="linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)"
            subtitle="On duty now"
            loading={loading}
            onClick={() => { window.location.href = '/schedule'; }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={{ xs: 2, sm: 2, md: 3 }}>
        {/* Unassigned Queue */}
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" fontWeight={700}>
                  Unassigned Queue
                </Typography>
                <Chip
                  label={`${stats?.unassigned_count ?? 0} tickets`}
                  size="small"
                  color="primary"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <Box sx={{ maxHeight: 500, overflowY: 'auto', pr: 1 }}>
                {loading ? (
                  [...Array(3)].map((_, i) => (
                    <Skeleton key={i} variant="rectangular" height={100} sx={{ mb: 2, borderRadius: 2 }} />
                  ))
                ) : unassignedTickets.length > 0 ? (
                  unassignedTickets.map((ticket) => (
                    <TicketItem key={ticket.id} ticket={ticket} />
                  ))
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">No unassigned tickets</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={7}>
          <Grid container spacing={{ xs: 2, sm: 2, md: 3 }}>
            {/* Recent Alerts */}
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Typography variant="h6" fontWeight={700}>
                      Recent Alerts
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {incomingUnviewed > 0 && <Chip label={`${incomingUnviewed} new`} size="small" sx={{ fontWeight: 700, bgcolor: CC.red, color: '#fff' }} />}
                      <Chip label={`${alerts.length + incoming.length} active`} size="small" color="warning" sx={{ fontWeight: 600 }} />
                    </Box>
                  </Box>
                  {/* incoming items: emails to tech@, customer pictures/documents, portal requests — click opens it and logs the view */}
                  {incoming.filter((a) => a.kind !== 'invalid').map((a) => <IncomingAlertItem key={a.id} a={a} onOpen={openIncoming} />)}
                  <TripVerifyDialog tripId={verifyTrip} onClose={(changed) => { setVerifyTrip(null); if (changed) loadIncoming(); }} />
                  {alerts.map((alert, index) => (
                    <AlertItem key={index} {...alert} />
                  ))}
                  {alerts.length + incoming.filter((a) => a.kind !== 'invalid').length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <Typography color="text.secondary">No active alerts — nothing has come in from customers, tech@ email or the portal.</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Ticket not valid: mail that named a CLOSED ticket — no reply sent, deleted 10 days after receipt (2026-09-04) */}
            {incoming.some((a) => a.kind === 'invalid') && (
              <Grid item xs={12}>
                <Card sx={{ borderTop: `3px solid ${CC.warn}` }}>
                  <CardContent sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" fontWeight={700}>Ticket not valid</Typography>
                        <Typography variant="caption" color="text.secondary">Emails that referenced a closed ticket. The sender got no reply; each is deleted with its files 10 days after receipt unless you assign it to an open ticket.</Typography>
                      </Box>
                      <Chip label={`${incoming.filter((a) => a.kind === 'invalid').length}`} size="small" sx={{ fontWeight: 700, bgcolor: CC.warn, color: '#fff' }} />
                    </Box>
                    {incoming.filter((a) => a.kind === 'invalid').map((a) => <IncomingAlertItem key={a.id} a={a} onOpen={openIncoming} />)}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Active Technicians */}
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Typography variant="h6" fontWeight={700}>
                      Active Technicians
                    </Typography>
                    <LocalShipping sx={{ color: 'primary.main' }} />
                  </Box>
                  {technicians.map((tech, index) => (
                    <Box key={index} onClick={() => { window.location.href = '/schedule'; }} sx={{ cursor: 'pointer' }}>
                      <TechnicianItem {...tech} />
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      
      {/* Create Ticket FAB */}
      <Fab color="primary" onClick={() => { window.location.href = '/tickets?action=create'; }}
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000 }}>
          <AddIcon />
        </Fab>
    </Box>
  );
}
